import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import {
  AgentOption,
  BulkUploadContextType,
  BulkUploadFile,
  BulkUploadFileStatus,
  BulkUploadProgress,
  BulkUploadProviderProps,
  BulkUploadValidationResult,
  FILE_SIZE_WARNING_THRESHOLD,
  MAX_BATCH_SIZE,
} from "@/types/bulk-upload";
import { getFileTypeInfo } from "@/lib/fileType";
import { bulkUploadReducer, createInitialBulkUploadState } from "./bulkUploadReducer";

const BulkUploadContext = createContext<BulkUploadContextType | undefined>(undefined);

const generateId = () => crypto.randomUUID();

export const BulkUploadProvider = ({ children }: BulkUploadProviderProps) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [state, dispatch] = useReducer(bulkUploadReducer, createInitialBulkUploadState());
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [availableAgents, setAvailableAgentsState] = useState<AgentOption[]>([]);

  // Use ref to track if currently processing to avoid multiple concurrent uploads
  const processingRef = useRef(false);
  const stateRef = useRef(state);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const hasUploadingFiles = state.files.some((file) => file.status === "uploading");

  // Animate progress for files being uploaded
  useEffect(() => {
    if (!hasUploadingFiles) return;

    const progressInterval = setInterval(() => {
      stateRef.current.files.forEach((f) => {
        if (f.status === "uploading" && f.progress < 90) {
          dispatch({
            type: "UPDATE_FILE",
            payload: { id: f.id, updates: { progress: Math.min(f.progress + 10, 90) } },
          });
        }
      });
    }, 500);

    return () => clearInterval(progressInterval);
  }, [hasUploadingFiles]);

  // Calculate progress
  const progress = useMemo((): BulkUploadProgress => {
    const files = state.files;
    const total = files.length;
    const completed = files.filter((f) => f.status === "completed").length;
    const failed = files.filter((f) => f.status === "failed").length;
    const inProgress = files.filter((f) => f.status === "uploading").length;
    const pending = files.filter((f) => f.status === "pending").length;

    const overallProgress = total > 0 ? Math.round(((completed + failed) / total) * 100) : 0;

    return { total, completed, failed, inProgress, pending, overallProgress };
  }, [state.files]);

  const isActive = state.isProcessing || state.files.some((f) => f.status === "pending" || f.status === "uploading");

  // Track if we've shown the completion toast
  const completionShownRef = useRef(false);

  // Detect when all processing is complete
  useEffect(() => {
    const files = state.files;
    if (files.length === 0) {
      completionShownRef.current = false;
      return;
    }

    const hasIncomplete = files.some((f) => f.status === "pending" || f.status === "uploading");

    if (!hasIncomplete && !completionShownRef.current) {
      completionShownRef.current = true;

      const completed = files.filter((f) => f.status === "completed").length;
      const failed = files.filter((f) => f.status === "failed").length;

      if (completed > 0 || failed > 0) {
        if (failed === 0) {
          toast.success(`Bulk upload complete: ${completed} files processed`);
        } else {
          toast.warning(`Bulk upload complete: ${completed} succeeded, ${failed} failed`);
        }

        queryClient.invalidateQueries({ queryKey: ["documents"] });
        queryClient.invalidateQueries({ queryKey: ["documents-infinite"] });
        queryClient.invalidateQueries({ queryKey: ["knowledge-base-docs"] });
      }
    }
  }, [state.files, queryClient]);

  // Process next file in queue
  const processNextFile = useCallback(async () => {
    if (processingRef.current || stateRef.current.isPaused) return;

    const pendingFiles = stateRef.current.files.filter((f) => f.status === "pending");
    if (pendingFiles.length === 0) {
      dispatch({ type: "SET_PROCESSING", payload: false });
      return;
    }

    processingRef.current = true;
    const file = pendingFiles[0];

    dispatch({ type: "UPDATE_FILE", payload: { id: file.id, updates: { status: "uploading", progress: 0 } } });

    try {
      const fileTypeInfo = getFileTypeInfo(file.file);
      if (!fileTypeInfo) {
        throw new Error(`Unsupported file type: ${file.name}`);
      }

      const formData = new FormData();
      formData.append("action", fileTypeInfo.action);
      formData.append("file", file.file);
      formData.append("userId", user?.id || "");

      if (stateRef.current.agentId) {
        formData.append("agentId", stateRef.current.agentId);
      }

      const { data, error } = await supabase.functions.invoke("doc-upload", {
        body: formData,
      });

      if (error || !data?.success) {
        throw new Error(error?.message || data?.error || "Upload failed");
      }

      dispatch({
        type: "UPDATE_FILE",
        payload: { id: file.id, updates: { status: "completed", documentId: data.document.id, progress: 100 } },
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      dispatch({
        type: "UPDATE_FILE",
        payload: { id: file.id, updates: { status: "failed", error: errorMessage } },
      });
    }

    processingRef.current = false;
    setTimeout(() => processNextFile(), 500);
  }, [user?.id]);

  // Add files to queue
  const addFiles = useCallback(
    (files: FileList | File[]): BulkUploadValidationResult => {
      const fileArray = Array.from(files);
      const errors: string[] = [];
      const warnings: string[] = [];
      const validFiles: File[] = [];
      const invalidFiles: Array<{ file: File; reason: string }> = [];

      const availableSlots = MAX_BATCH_SIZE - state.files.length;

      if (fileArray.length > MAX_BATCH_SIZE) {
        errors.push(`Maximum ${MAX_BATCH_SIZE} files per batch. You selected ${fileArray.length}.`);
        return { valid: false, errors, warnings, validFiles: [], invalidFiles: [] };
      }


      fileArray.forEach((file) => {
        const fileTypeInfo = getFileTypeInfo(file);

        if (!fileTypeInfo) {
          const ext = file.name.split(".").pop()?.toLowerCase() || "unknown";
          invalidFiles.push({ file, reason: `Unsupported file type: .${ext}` });
          return;
        }

        if (file.size > FILE_SIZE_WARNING_THRESHOLD) {
          warnings.push(`${file.name}: Large file (${(file.size / 1024 / 1024).toFixed(1)}MB) - may take longer to process`);
        }

        validFiles.push(file);
      });

      const filesToAdd = errors.length > 0 ? [] : validFiles.slice(0, availableSlots === Infinity ? validFiles.length : availableSlots);

      if (filesToAdd.length > 0) {
        const newFiles: BulkUploadFile[] = filesToAdd.map((file) => {
          const fileTypeInfo = getFileTypeInfo(file)!;
          return {
            id: generateId(),
            file,
            name: file.name,
            size: file.size,
            type: fileTypeInfo.type,
            status: "pending" as BulkUploadFileStatus,
            progress: 0,
            addedAt: Date.now(),
          };
        });

        dispatch({ type: "ADD_FILES", payload: newFiles });
        setIsPanelOpen(true);
      }

      return {
        valid: errors.length === 0 && invalidFiles.length === 0,
        errors,
        warnings,
        validFiles: filesToAdd,
        invalidFiles,
      };
    },
    [state.files],
  );

  // Action handlers
  const removeFile = useCallback((fileId: string) => {
    dispatch({ type: "REMOVE_FILE", payload: fileId });
  }, []);

  const clearQueue = useCallback(() => {
    dispatch({ type: "CLEAR_QUEUE" });
    setIsPanelOpen(false);
  }, []);

  const clearCompleted = useCallback(() => {
    dispatch({ type: "CLEAR_COMPLETED" });
  }, []);

  const startUpload = useCallback(() => {
    if (!user?.id) {
      toast.error("Please sign in to upload files");
      return;
    }

    const pendingCount = stateRef.current.files.filter((f) => f.status === "pending").length;
    if (pendingCount === 0) {
      toast.info("No files to upload");
      return;
    }

    dispatch({ type: "SET_PROCESSING", payload: true });
    dispatch({ type: "SET_PAUSED", payload: false });
    toast.info(`Starting upload of ${pendingCount} files...`);
    processNextFile();
  }, [user?.id, processNextFile]);

  const pauseUpload = useCallback(() => {
    dispatch({ type: "SET_PAUSED", payload: true });
    toast.info("Upload paused");
  }, []);

  const resumeUpload = useCallback(() => {
    dispatch({ type: "SET_PAUSED", payload: false });
    toast.info("Upload resumed");
    processNextFile();
  }, [processNextFile]);

  const retryFailed = useCallback(() => {
    dispatch({ type: "RETRY_FAILED" });
    if (!state.isProcessing) {
      startUpload();
    }
  }, [state.isProcessing, startUpload]);

  const setAgent = useCallback((agent: { id?: string; name?: string }) => {
    dispatch({ type: "SET_AGENT", payload: agent });
  }, []);

  const setAvailableAgents = useCallback((agents: AgentOption[]) => {
    setAvailableAgentsState(agents);
  }, []);

  const openPanel = useCallback(() => setIsPanelOpen(true), []);
  const closePanel = useCallback(() => setIsPanelOpen(false), []);
  const togglePanel = useCallback(() => setIsPanelOpen((prev) => !prev), []);

  const value: BulkUploadContextType = {
    state,
    progress,
    isActive,
    isPanelOpen,
    availableAgents,
    addFiles,
    removeFile,
    clearQueue,
    clearCompleted,
    startUpload,
    pauseUpload,
    resumeUpload,
    retryFailed,
    setAgent,
    setAvailableAgents,
    openPanel,
    closePanel,
    togglePanel,
  };

  return <BulkUploadContext.Provider value={value}>{children}</BulkUploadContext.Provider>;
};

export const useBulkUpload = (): BulkUploadContextType => {
  const context = useContext(BulkUploadContext);
  if (!context) {
    throw new Error("useBulkUpload must be used within a BulkUploadProvider");
  }
  return context;
};
